import React from "react";
import ExperienceCard from "@/components/blocks/ExperienceCard";
import ScrollReveal from "@/components/ScrollReveal";
import BrassRule from "@/components/BrassRule";

function CityBlock({ cityCode, cityName, cityImage, experiences, eyebrow, countLabel, alt }) {
  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-20">
      <div
        className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center ${
          alt ? "md:[&>:first-child]:col-start-7" : ""
        }`}
      >
        <ScrollReveal className={alt ? "md:col-span-6 md:col-start-7" : "md:col-span-6"}>
          <div
            className="relative w-full overflow-hidden bg-mist"
            style={{ aspectRatio: "4 / 5" }}
          >
            <picture>
              {/\.jpe?g$/i.test(cityImage || "") && (
                <source srcSet={cityImage.replace(/\.jpe?g$/i, ".webp")} type="image/webp" />
              )}
              <img
                src={cityImage}
                alt={cityName}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover dest-kb"
              />
            </picture>
          </div>
        </ScrollReveal>
        <ScrollReveal
          delay={120}
          className={
            alt
              ? "md:col-span-6 md:col-start-1 md:row-start-1"
              : "md:col-span-6"
          }
        >
          <p className="eyebrow text-brass">{eyebrow}</p>
          <h3
            className="font-display text-charcoal mt-4 leading-[1.0]"
            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            {cityName}
          </h3>
          <p
            className="italic-quote text-charcoal/85 mt-4"
            style={{ fontSize: "clamp(1.1rem, 1.4vw, 1.3rem)" }}
          >
            {experiences.length} {countLabel}
          </p>
          <BrassRule className="mt-6" width={48} />
        </ScrollReveal>
      </div>
      <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
        {experiences.map((exp, i) => (
          <ScrollReveal key={exp.slug} delay={(i % 3) * 90}>
            <ExperienceCard experience={exp} />
          </ScrollReveal>
        ))}
      </div>
    </div>
  );
}

/**
 * For each city in the country meta, render a block: city portrait + name +
 * count, then a 3-column grid of ExperienceCard for that city. Cities with
 * no experiences are skipped.
 */
export default function CountryByCity({
  meta,
  cityLabels,
  byCity,
  lang,
  eyebrow,
  countLabel,
}) {
  return (
    <section
      className="bg-pearl"
      style={{ backgroundColor: "var(--color-pearl)" }}
      data-testid="by-city-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 pt-16 pb-6 text-center">
        <p className="eyebrow text-charcoal/80">{eyebrow}</p>
      </div>
      {meta.cities.map((cityCode, idx) => {
        const cityExp = byCity[cityCode] || [];
        if (!cityExp.length) return null;
        const cityName = cityLabels[cityCode]?.[lang] || cityCode;
        return (
          <CityBlock
            key={cityCode}
            cityCode={cityCode}
            cityName={cityName}
            cityImage={meta.city_image[cityCode]}
            experiences={cityExp}
            eyebrow={eyebrow}
            countLabel={countLabel}
            alt={idx % 2 === 1}
          />
        );
      })}
    </section>
  );
}
